import type { ReactNode } from "react";

/**
 * Das Zeichen der webradiosuite.
 *
 * Ein Mast in der Mitte, links und rechts je zwei Wellen: ein Sender, der
 * sendet. Mehr nicht – das Zeichen muss bei 16 Pixeln in der Lesezeichenleiste
 * noch erkennbar sein, und dort überlebt kein Detail.
 *
 * Die Wellen nehmen `currentColor` an, der Mast die Hausfarbe. So sitzt das
 * Zeichen in der dunklen Seitenleiste genauso wie auf hellem Grund, ohne dass
 * es zwei Fassungen bräuchte.
 *
 * Dieselbe Geometrie steht im Studio (studio-rs/crates/wrstudio-ui/src/logo.rs)
 * und im App-Symbol (studio-rs/packaging/make_icon.py). Wer hier etwas ändert,
 * ändert es dort mit – sonst laufen die drei auseinander.
 */
export function Logo({ size = 28, className = "" }: { size?: number; className?: string }): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      {/* Der Mast. Die einzige gefüllte Form – sie trägt das Zeichen. */}
      <rect x="11.1" y="8.4" width="1.8" height="7.2" rx="0.9" fill="#ff5500" />
      {/* Innere Wellen */}
      <path d="M14.29 8.72a4 4 0 0 1 0 6.56" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <path d="M9.71 8.72a4 4 0 0 0 0 6.56" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      {/* Äußere Wellen, etwas zurückgenommen: das gibt dem Zeichen Tiefe. */}
      <path d="M16.02 6.27a7 7 0 0 1 0 11.46" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" opacity="0.55" />
      <path d="M7.98 6.27a7 7 0 0 0 0 11.46" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" opacity="0.55" />
    </svg>
  );
}

/**
 * Zeichen und Name nebeneinander – für die Seitenleiste.
 *
 * „webradio" trägt den Namen, „suite" steht halblaut daneben: zusammen ein
 * Wort, gelesen als zwei.
 */
export function LogoZeile({ className = "" }: { className?: string }): JSX.Element {
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Logo size={30} />
      <span className="text-lg font-semibold leading-none tracking-tight">
        webradio<span className="font-normal opacity-70">suite</span>
      </span>
    </div>
  );
}

/**
 * Rahmen für alles vor und außerhalb der Suite: Anmeldung, Ersteinrichtung,
 * Gast-Einladung.
 *
 * Links das Formular in einer Karte, rechts ein dunkles Markenfeld mit dem
 * Zeichen, einem Satz und ruhigen Sendewellen. Auf dem Telefon bleibt nur das
 * Formular – dort zählt, dass man schnell hineinkommt.
 */
export function AuthRahmen({
  children,
  titel = "Euer Radio. Alles an einem Ort.",
  text = "Sendeplan, Studio, Mediathek und Hörerseite – eine Suite für den ganzen Sender.",
  punkte = ["Live aus dem Studio oder automatisch", "Hörer mitmachen lassen", "Statistik und Meldelisten"],
  breit = false,
}: {
  children: ReactNode;
  titel?: string;
  text?: string;
  punkte?: string[];
  /** Breitere Formularkarte (z. B. Ersteinrichtung). */
  breit?: boolean;
}): JSX.Element {
  return (
    <div className="grid min-h-full bg-app lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <div className="flex min-h-full flex-col px-4 py-6 sm:px-8">
        <div className="text-primary lg:invisible">
          <LogoZeile />
        </div>
        <div className="flex flex-1 items-center justify-center py-8">
          <div className={`w-full animate-fade-in ${breit ? "max-w-md" : "max-w-sm"}`}>{children}</div>
        </div>
        <div className="text-center text-xs text-muted">webradiosuite</div>
      </div>

      {/* Das Markenfeld – rein aus CSS und SVG, ohne Bilddateien. */}
      <div className="relative m-3 hidden overflow-hidden rounded-2xl bg-[var(--bg-sidebar)] text-white lg:flex lg:flex-col">
        <div
          className="pointer-events-none absolute inset-0"
          aria-hidden="true"
          style={{
            backgroundImage:
              "radial-gradient(700px 420px at 85% 10%, rgb(var(--brand-primary-rgb) / 0.35), transparent 60%)," +
              "radial-gradient(600px 400px at 0% 100%, rgb(var(--brand-primary-rgb) / 0.18), transparent 60%)",
          }}
        />
        {/* Sendewellen: Kreisbögen um einen Punkt rechts unten, wie das Zeichen, nur groß. */}
        <svg className="pointer-events-none absolute -bottom-40 -right-40 h-[720px] w-[720px] text-white" viewBox="0 0 720 720" fill="none" aria-hidden="true">
          {[80, 150, 220, 290, 360].map((r, i) => (
            <circle key={r} cx="360" cy="360" r={r} stroke="currentColor" strokeWidth="1.5" opacity={0.14 - i * 0.022} />
          ))}
          <circle cx="360" cy="360" r="28" style={{ fill: "rgb(var(--brand-primary-rgb))" }} opacity="0.18" />
          <circle cx="360" cy="360" r="7" style={{ fill: "rgb(var(--brand-primary-rgb))" }} />
        </svg>
        <div className="relative px-12 pt-12">
          <LogoZeile />
        </div>
        <div className="relative mt-auto max-w-lg px-12 pb-14">
          <h2 className="text-[2.1rem] font-semibold leading-[1.15] tracking-tight [text-wrap:balance]">{titel}</h2>
          <p className="mt-4 text-[0.95rem] leading-relaxed text-white/65">{text}</p>
          {punkte.length > 0 && (
            <ul className="mt-8 space-y-3">
              {punkte.map((p) => (
                <li key={p} className="flex items-center gap-3 text-sm text-white/80">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-primary/20 text-brand-primary ring-1 ring-inset ring-brand-primary/30">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l4 4 10-10" /></svg>
                  </span>
                  {p}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
